import { useState, type KeyboardEvent } from 'react';
import {
  Bars3Icon,
  CheckCircleIcon,
  PlayCircleIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  PlayCircleIcon as PlayCircleSolidIcon,
} from '@heroicons/react/24/solid';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { STATE_PALETTE } from '../../utils/statePalette';
import { StateColorPickerPopover } from './StateColorPickerPopover';

export interface EditorState {
  /** Stable id for keying + drag/drop. Negative ids permitted (optimistic creates from useCreateProjectState — Phase 20 plan 20-03). */
  id: number;
  name: string;
  ordering: number;
  color: string; // '#RRGGBB'
  /** 260723-tst — terminal flag (project mode only; templates omit it). */
  isTerminal?: boolean;
  /** 260726-wip — "work in progress" flag (project mode only; templates omit it). */
  isInProgress?: boolean;
}

export interface StateListEditorProps {
  states: EditorState[]; // ordered (caller sorts by ordering)
  mode: 'template' | 'project';
  /** When true: hides all editing controls (drag, rename, recolor, delete, add). Used for built-in Minimal in template mode (D-04). */
  readOnly?: boolean;
  onAdd: (name: string, color: string) => void | Promise<void>;
  onRename: (stateId: number, newName: string) => void | Promise<void>;
  onRecolor: (stateId: number, newColor: string) => void | Promise<void>;
  onDelete: (stateId: number) => void | Promise<void>;
  onReorder: (orderedStateIds: number[]) => void | Promise<void>;
  /**
   * 260723-tst — toggle a state's terminal flag. Project mode only; when
   * omitted (template mode) the terminal toggle is not rendered.
   */
  onToggleTerminal?: (stateId: number, next: boolean) => void | Promise<void>;
  /**
   * 260726-wip — toggle a state's "work in progress" flag. Project mode only;
   * when omitted (template mode) the WIP toggle is not rendered. Mutually
   * exclusive with the terminal flag (the parent handler clears the other).
   */
  onToggleInProgress?: (stateId: number, next: boolean) => void | Promise<void>;
  /**
   * MKS-345 — extra per-row controls, rendered between the name and the
   * delete button. Added so the lead pipeline (whose stages carry a kind and
   * a win probability the task-state flags know nothing about) can reuse this
   * editor as-is instead of forking it. Omitted everywhere else, so no
   * existing caller changes.
   */
  extraControls?: (stateId: number) => React.ReactNode;
}

/**
 * Shared state-list editor (Phase 20 D-11..D-14).
 *
 * Two modes (D-11) drive small UX differences but share the row layout:
 *   - 'template': used by the admin template edit page (plan 20-07).
 *     The parent buffers edits client-side and dispatches a single Save.
 *   - 'project': used by the per-project Task States section (plan 20-08).
 *     Each callback fires its own optimistic mutation (plan 20-03 hooks);
 *     the parent surfaces 409 STATE_IN_USE errors via AppToast (D-20).
 *
 * `readOnly={true}` (D-04) renders a static state list — no drag handles,
 * no rename inputs, no recolor popovers, no delete buttons, no + Add row.
 * Used for the built-in Minimal template in template mode.
 *
 * @dnd-kit/sortable is already a project dep (used by leads kanban —
 * frontend/src/components/leads/LeadKanbanBoard.tsx). No new deps.
 */
export function StateListEditor(props: StateListEditorProps) {
  const {
    states,
    mode,
    readOnly = false,
    onAdd,
    onRename,
    onRecolor,
    onDelete,
    onReorder,
    onToggleTerminal,
    onToggleInProgress,
    extraControls,
  } = props;

  const [editingId, setEditingId] = useState<number | null>(null);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = states.findIndex((s) => s.id === active.id);
    const newIdx = states.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(states, oldIdx, newIdx);
    void onReorder(reordered.map((s) => s.id));
  }

  function commitRename(stateId: number, newName: string) {
    const trimmed = newName.trim();
    const original = states.find((s) => s.id === stateId);
    if (trimmed && original && trimmed !== original.name) {
      void onRename(stateId, trimmed);
    }
    setEditingId(null);
  }

  function commitAdd(name: string) {
    const trimmed = name.trim();
    if (trimmed) {
      const nextColor = STATE_PALETTE[states.length % STATE_PALETTE.length] ?? STATE_PALETTE[0];
      void onAdd(trimmed, nextColor);
    }
    setIsAdding(false);
  }

  // Empty state (D-14) — only path that drops the dnd context.
  if (states.length === 0 && !readOnly) {
    return (
      <div className="space-y-3" data-mode={mode}>
        <p className="italic text-sm text-gray-500 py-4">
          No states yet — add your first state below.
        </p>
        <AddStateControl isAdding={isAdding} setIsAdding={setIsAdding} commitAdd={commitAdd} />
      </div>
    );
  }

  // ReadOnly mode — static list, no DndContext, no controls (D-04).
  if (readOnly) {
    return (
      <div className="space-y-2" data-mode={mode} data-readonly="true">
        {states.map((state) => (
          <div
            key={state.id}
            className="flex items-center gap-3 py-2 px-3 bg-gray-50 border border-gray-200 rounded"
          >
            <div
              className="h-5 w-5 rounded border border-gray-300"
              style={{ backgroundColor: state.color }}
              aria-label={`color ${state.color}`}
            />
            <span className="text-sm font-medium text-gray-900">{state.name}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-mode={mode}>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={states.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {states.map((state) => (
              <StateRow
                key={state.id}
                state={state}
                isEditing={editingId === state.id}
                isPopoverOpen={openPopoverId === state.id}
                onStartEdit={() => setEditingId(state.id)}
                onCommitRename={(newName) => commitRename(state.id, newName)}
                onCancelEdit={() => setEditingId(null)}
                onTogglePopover={() =>
                  setOpenPopoverId((prev) => (prev === state.id ? null : state.id))
                }
                onClosePopover={() => setOpenPopoverId(null)}
                onPickColor={(hex) => void onRecolor(state.id, hex)}
                onDelete={() => void onDelete(state.id)}
                onToggleTerminal={
                  onToggleTerminal
                    ? () => void onToggleTerminal(state.id, !(state.isTerminal ?? false))
                    : undefined
                }
                onToggleInProgress={
                  onToggleInProgress
                    ? () => void onToggleInProgress(state.id, !(state.isInProgress ?? false))
                    : undefined
                }
                extraControls={extraControls}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <AddStateControl isAdding={isAdding} setIsAdding={setIsAdding} commitAdd={commitAdd} />
    </div>
  );
}

interface StateRowProps {
  state: EditorState;
  isEditing: boolean;
  isPopoverOpen: boolean;
  onStartEdit: () => void;
  onCommitRename: (newName: string) => void;
  onCancelEdit: () => void;
  onTogglePopover: () => void;
  onClosePopover: () => void;
  onPickColor: (hex: string) => void;
  onDelete: () => void;
  /** 260723-tst — flip the terminal flag; undefined = toggle not shown. */
  onToggleTerminal?: () => void;
  /** 260726-wip — flip the "work in progress" flag; undefined = toggle not shown. */
  onToggleInProgress?: () => void;
  /** MKS-345 — caller-supplied controls, rendered before the delete button. */
  extraControls?: (stateId: number) => React.ReactNode;
}

function StateRow({
  state,
  isEditing,
  isPopoverOpen,
  onStartEdit,
  onCommitRename,
  onCancelEdit,
  onTogglePopover,
  onClosePopover,
  onPickColor,
  onDelete,
  onToggleTerminal,
  onToggleInProgress,
  extraControls,
}: StateRowProps) {
  const isTerminal = state.isTerminal ?? false;
  const isInProgress = state.isInProgress ?? false;
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: state.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  function onNameKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).value = state.name;
      onCancelEdit();
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex items-center gap-3 py-2 px-3 bg-white border border-gray-200 rounded"
      data-state-id={state.id}
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className="text-gray-400 hover:text-gray-600 cursor-grab"
        {...attributes}
        {...listeners}
      >
        <Bars3Icon className="h-4 w-4" />
      </button>

      <div className="relative">
        <button
          type="button"
          aria-label="Choose color"
          className="h-5 w-5 rounded border border-gray-300"
          style={{ backgroundColor: state.color }}
          // WR-01: stopPropagation on mousedown so the popover's
          // click-outside listener doesn't fire FIRST and close the
          // popover before this button's onClick toggles it. Without
          // this, clicking the same swatch that opened the popover
          // produced an open→close→open sequence and the popover
          // appeared "stuck" open.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onTogglePopover}
        />
        {isPopoverOpen && (
          <StateColorPickerPopover
            currentColor={state.color}
            onPick={onPickColor}
            onClose={onClosePopover}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            type="text"
            autoFocus
            defaultValue={state.name}
            aria-label="State name"
            onBlur={(e) => {
              // WR-06: if focus is moving to this row's Delete button (or
              // any element marked as a "skip-rename" target), suppress the
              // rename commit. Otherwise the rename PATCH races the delete
              // and the row ends up half-updated or with stepped-on caches.
              const next = e.relatedTarget as HTMLElement | null;
              if (next?.dataset.skipRename === 'true') {
                onCancelEdit();
                return;
              }
              onCommitRename(e.currentTarget.value);
            }}
            onKeyDown={onNameKeyDown}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={onStartEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onStartEdit();
              }
            }}
            className="text-sm font-medium text-gray-900 hover:text-primary-600 cursor-text block truncate"
          >
            {state.name}
          </span>
        )}
      </div>

      {/* 260726-wip — work-in-progress toggle: mark this state as actively
          being worked (Todo / In Progress / ...). Solid blue play when on, gray
          outline when off. Mutually exclusive with the terminal flag. */}
      {onToggleInProgress && (
        <button
          type="button"
          aria-label={
            isInProgress
              ? `Unmark ${state.name} as work in progress`
              : `Mark ${state.name} as work in progress`
          }
          aria-pressed={isInProgress}
          title={
            isInProgress
              ? 'Work-in-progress state (task actively being worked) — click to unmark'
              : 'Mark as work-in-progress state (task actively being worked)'
          }
          data-skip-rename="true"
          onClick={onToggleInProgress}
          className={isInProgress ? 'text-blue-600' : 'text-gray-300 hover:text-gray-500'}
        >
          {isInProgress ? (
            <PlayCircleSolidIcon className="h-5 w-5" />
          ) : (
            <PlayCircleIcon className="h-5 w-5" />
          )}
        </button>
      )}

      {/* 260723-tst — terminal toggle: mark this state as "no longer in
          progress" (Done / Archived / Cancelled / ...). Solid green check when
          on, gray outline when off. Suppresses the age 💩 emoji for tasks
          sitting in this column. */}
      {onToggleTerminal && (
        <button
          type="button"
          aria-label={isTerminal ? `Unmark ${state.name} as final` : `Mark ${state.name} as final`}
          aria-pressed={isTerminal}
          title={
            isTerminal
              ? 'Final state (task no longer in progress) — click to unmark'
              : 'Mark as final state (task no longer in progress)'
          }
          data-skip-rename="true"
          onClick={onToggleTerminal}
          className={isTerminal ? 'text-green-600' : 'text-gray-300 hover:text-gray-500'}
        >
          {isTerminal ? (
            <CheckCircleSolidIcon className="h-5 w-5" />
          ) : (
            <CheckCircleIcon className="h-5 w-5" />
          )}
        </button>
      )}

      {extraControls?.(state.id)}

      <button
        type="button"
        aria-label={`Delete ${state.name}`}
        // WR-06: data-skip-rename signals to the rename input's onBlur
        // handler to skip the PATCH dispatch when the user clicks Delete
        // mid-edit (otherwise rename + delete race on the same row).
        data-skip-rename="true"
        onClick={onDelete}
        className="text-gray-400 hover:text-red-600"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

interface AddStateControlProps {
  isAdding: boolean;
  setIsAdding: (v: boolean) => void;
  commitAdd: (name: string) => void;
}

function AddStateControl({ isAdding, setIsAdding, commitAdd }: AddStateControlProps) {
  if (!isAdding) {
    return (
      <button
        type="button"
        onClick={() => setIsAdding(true)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
      >
        <PlusIcon className="h-4 w-4" />
        Add state
      </button>
    );
  }
  return (
    <input
      type="text"
      autoFocus
      placeholder="New state name"
      aria-label="New state name"
      onBlur={(e) => commitAdd(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setIsAdding(false);
        }
      }}
      className="rounded border border-gray-300 px-2 py-1 text-sm"
    />
  );
}
